import { PublicationError } from '@streamlain/doc-publication-contract';
import { map, merge, Observable, of, switchMap, throttleTime } from 'rxjs';

import type {
  AggregateOptions,
  AggregateResult,
  IndexerDocument,
  IndexerSchema,
  Query,
  SearchOptions,
  SearchResult,
} from '../../../storage';
import { INDEXER_TABLES } from '../../../storage';
import { fromPromise } from '../../../utils/from-promise';
import { takeUntilAbort } from '../../../utils/take-until-abort';
import type { IndexDocumentInput } from './access';
import type { DataStruct } from './data-struct';
import {
  EncryptedIndexSegments,
  type IndexWriteBatch,
} from './encrypted-segments';
import type { IndexSegmentStore } from './segment-store';
import { exhaustMapWithTrailing } from './utils';

export const emptyIndexWriteBatch = (): IndexWriteBatch => ({
  deleteByQueries: [],
  deletes: [],
  inserts: [],
  updates: [],
});
/** One empty write batch per table of the index. */
export const emptyIndexWriteBatches = () =>
  Object.fromEntries(
    INDEXER_TABLES.map(table => [table, emptyIndexWriteBatch()])
  ) as Record<keyof IndexerSchema, IndexWriteBatch>;
export const hasIndexWrites = (batch: IndexWriteBatch) =>
  batch.deleteByQueries.length > 0 ||
  batch.deletes.length > 0 ||
  batch.inserts.length > 0 ||
  batch.updates.length > 0;

/**
 * The public schema has string field names. Narrow at the port boundary;
 * `isolate` detaches the caller's Map/arrays from queued account writes.
 */
export function indexDocumentInput<T extends keyof IndexerSchema>(
  document: IndexerDocument<T>,
  isolate: boolean
): IndexDocumentInput {
  for (const field of document.fields.keys())
    if (typeof field !== 'string')
      throw new PublicationError('FORMAT_MISMATCH');
  const input = document as unknown as IndexDocumentInput;
  return isolate
    ? { id: input.id, fields: structuredClone(input.fields) }
    : input;
}

/**
 * Account-bound search over sealed index segments, shared by every storage
 * backend. Writes queue per table and flush as serialized sealed batches;
 * reads run against the ready plaintext RAM index under the source authority.
 */
export class AccountSearchIndex {
  readonly segments: EncryptedIndexSegments;
  private pending: Record<keyof IndexerSchema, IndexWriteBatch> =
    emptyIndexWriteBatches();
  private refreshTail: Promise<void> = Promise.resolve();
  constructor(
    store: IndexSegmentStore,
    private readonly data: DataStruct,
    private readonly updated: (
      table: keyof IndexerSchema
    ) => Observable<unknown>,
    private readonly emitUpdated: (table: keyof IndexerSchema) => void
  ) {
    this.segments = new EncryptedIndexSegments(store, data);
    this.segments.source.onInvalidate(() => {
      for (const table of INDEXER_TABLES)
        this.pending[table] = emptyIndexWriteBatch();
    });
  }
  get source() {
    return this.segments.source;
  }

  search<T extends keyof IndexerSchema, const O extends SearchOptions<T>>(
    table: T,
    query: Query<T>,
    options?: O
  ): Promise<SearchResult<T, O>> {
    return this.segments.read(tx =>
      this.data.search(tx, table, query, options)
    );
  }
  aggregate<T extends keyof IndexerSchema, const O extends AggregateOptions<T>>(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O
  ): Promise<AggregateResult<T, O>> {
    return this.segments.read(tx =>
      this.data.aggregate(tx, table, query, field as string, options)
    );
  }
  /**
   * Observable read through the storage's public `read`, which returns only
   * after the authority check that follows its work; every output then still
   * needs a current lease, without retrying a locked source.
   */
  read$<T>(table: keyof IndexerSchema, read: () => Promise<T>): Observable<T> {
    const source = this.source;
    return fromPromise(signal => source.awaitReady(signal)).pipe(
      switchMap(lease =>
        merge(of(1), this.updated(table)).pipe(
          throttleTime(3000, undefined, { leading: true, trailing: true }),
          // The read starts on subscribe: a lease that ends first leaves no
          // read without a reader.
          exhaustMapWithTrailing(() => fromPromise(() => read())),
          map(value => {
            source.assertCurrent(lease);
            return value;
          }),
          takeUntilAbort(lease.signal)
        )
      )
    );
  }

  deleteByQuery<T extends keyof IndexerSchema>(table: T, query: Query<T>) {
    this.pending[table].deleteByQueries.push(structuredClone(query));
  }
  insert<T extends keyof IndexerSchema>(
    table: T,
    document: IndexerDocument<T>
  ) {
    this.pending[table].inserts.push(indexDocumentInput(document, true));
  }
  delete(table: keyof IndexerSchema, id: string) {
    this.pending[table].deletes.push(id);
  }
  update<T extends keyof IndexerSchema>(
    table: T,
    document: IndexerDocument<T>
  ) {
    this.pending[table].updates.push(indexDocumentInput(document, true));
  }
  refresh(table?: keyof IndexerSchema): Promise<void> {
    const work = this.refreshTail.then(async () => {
      for (const current of table ? [table] : INDEXER_TABLES) {
        const pending = this.pending[current];
        // Detach exactly this batch so writes arriving during crypto are retained.
        this.pending[current] = emptyIndexWriteBatch();
        await this.segments.flush(current, pending);
        this.emitUpdated(current);
      }
    });
    this.refreshTail = work.then(
      () => {},
      () => {}
    );
    return work;
  }
  async refreshIfNeed() {
    for (const table of INDEXER_TABLES)
      if (hasIndexWrites(this.pending[table])) await this.refresh(table);
  }
}
